/**
 * Auth-Guard — Token + UserContext-Probe (Phase 3.5 §3, wired mit Router §4).
 *
 * Pattern:
 *   - `probeAuth()`: returnt UserContext oder null. Kein Redirect. Genutzt vom
 *     Bootstrap in main.ts um zu entscheiden "Login-Page oder App rendern".
 *   - `requireAuth()`: returnt UserContext oder leitet zu Login. Genutzt von
 *     Router-Routes als ersten Schritt einer guarded Render-Function.
 *
 * Drift zu `getUserContext()` aus api/client.ts:
 *   - getUserContext() macht Auto-Redirect bei 401 (für API-Calls mid-session)
 *   - probeAuth() macht KEINEN Redirect (für initial Bootstrap-Check, sonst
 *     Loop zwischen Login-Page und Google)
 */

import { WORKER_URL } from "../api/client";
import type { ApiResponseError, ApiResponseOk, UserContext } from "../api/types";
import { clearToken, getToken } from "./token";
import { redirectToLogin } from "./login";

/**
 * Probe ob aktueller Token valid + lädt UserContext.
 *
 * - Kein Token → null (Caller rendert Login-Page)
 * - Token + 401 → Token clearen + null
 * - Token + Network-Fail → null (Caller kann Error-State zeigen)
 * - Token + 200 → UserContext
 */
export async function probeAuth(): Promise<UserContext | null> {
  const token = getToken();
  if (!token) return null;

  let res: Response;
  try {
    res = await fetch(`${WORKER_URL}/api/v1/auth/me`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch (err) {
    console.warn(`[probeAuth] Network-Fail: ${(err as Error).message}`);
    return null;
  }

  if (res.status === 401) {
    clearToken();
    return null;
  }

  let data: ApiResponseOk<UserContext> | ApiResponseError;
  try {
    data = (await res.json()) as ApiResponseOk<UserContext> | ApiResponseError;
  } catch {
    return null;
  }

  if (data.status === "error") {
    // 403 = Token gültig (Google-verifiziert), aber kein MA-Profil. Token NICHT
    // clearen — der Bootstrap-Claim-Flow (Phase 7.7) braucht ihn für die
    // Profil-Selbstzuordnung. Bei echtem Abbruch clear't der Bootstrap selbst.
    return null;
  }

  return data.result;
}

/**
 * Garantiert UserContext oder leitet zu Google-Login + wirft.
 * Genutzt am Anfang jeder Router-Route die Auth braucht.
 *
 * Aufrufer fängt den Throw nicht — Redirect ist Hard-Navigation,
 * Code danach läuft eh nicht mehr.
 */
export async function requireAuth(): Promise<UserContext> {
  const user = await probeAuth();
  if (!user) {
    redirectToLogin();
    throw new Error("AUTH_REQUIRED");
  }
  return user;
}
